/**
 * Atlas PM · 项目星图 · 节点几何与材质
 *
 * 对照原型 build() 里节点相关部分：
 *   - 项目核心：八面体 + 两圈外环 + 发光
 *   - 任务节点：二十面体（已冻结用立方体），状态着色
 *   - 每个节点：光晕 Sprite + 绕节点星轨
 *   - 文字标签：Canvas 贴图 Sprite
 */

import * as THREE from 'three';
import type { StarLayoutNode } from './layout';

export const CENTER_COLOR = 0x5ee7ff;

/* ── 共享径向光晕贴图 ── */
let glowTexCache: THREE.Texture | null = null;

export function getGlowTexture(): THREE.Texture {
  if (glowTexCache) return glowTexCache;
  const s = 128;
  const cv = document.createElement('canvas');
  cv.width = cv.height = s;
  const ctx = cv.getContext('2d');
  if (ctx) {
    const g = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.24, 'rgba(255,255,255,.55)');
    g.addColorStop(0.55, 'rgba(255,255,255,.14)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, s, s);
  }
  glowTexCache = new THREE.CanvasTexture(cv);
  return glowTexCache;
}

/* ── 文字标签（Canvas 贴图 Sprite） ── */
export function makeLabelSprite(
  text: string,
  color = '#9fc4e0',
  fontSize = 40,
): THREE.Sprite {
  const pad = 12;
  const cv = document.createElement('canvas');
  const measure = cv.getContext('2d');
  const font = `${fontSize}px "PingFang SC", "Hiragino Sans GB", sans-serif`;
  if (measure) measure.font = font;
  const w = Math.ceil((measure?.measureText(text).width ?? text.length * fontSize) + pad * 2);
  const h = fontSize + pad * 1.6;
  cv.width = w;
  cv.height = h;

  const ctx = cv.getContext('2d');
  if (ctx) {
    ctx.font = font;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = color;
    ctx.shadowColor = 'rgba(0,0,0,.85)';
    ctx.shadowBlur = 8;
    ctx.fillText(text, w / 2, h / 2);
  }

  const tex = new THREE.CanvasTexture(cv);
  tex.minFilter = THREE.LinearFilter;
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: tex,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      opacity: 0.9,
    }),
  );
  const k = 0.035;
  sprite.scale.set(w * k, h * k, 1);
  return sprite;
}

/* ── 项目核心：八面体 + 双外环 ── */
export function createCenterMesh(): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.OctahedronGeometry(2.3, 1),
    new THREE.MeshStandardMaterial({
      color: CENTER_COLOR,
      emissive: 0x1a6a9a,
      emissiveIntensity: 1.6,
      metalness: 0.5,
      roughness: 0.28,
      flatShading: true,
    }),
  );

  const ringMat = new THREE.MeshBasicMaterial({
    color: CENTER_COLOR,
    transparent: true,
    opacity: 0.42,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const ring = new THREE.Mesh(new THREE.TorusGeometry(4.0, 0.13, 8, 64), ringMat);
  ring.rotation.x = Math.PI / 2.3;
  mesh.add(ring);

  const ring2 = ring.clone();
  ring2.rotation.set(Math.PI / 1.7, 0.6, 0);
  ring2.scale.setScalar(0.72);
  mesh.add(ring2);

  return mesh;
}

/* ── 任务节点 ── */
export function createTaskMesh(node: StarLayoutNode): THREE.Mesh {
  const isFrozen = node.state === 'FROZEN';
  const geometry = isFrozen
    ? new THREE.BoxGeometry(1.5, 1.5, 1.5)
    : new THREE.IcosahedronGeometry(1, 2);
  const overdue = (node.task?.overdueDays ?? 0) > 0;

  const mesh = new THREE.Mesh(
    geometry,
    new THREE.MeshStandardMaterial({
      color: node.color,
      emissive: node.color,
      emissiveIntensity: overdue ? 0.95 : 0.5,
      metalness: 0.42,
      roughness: 0.34,
      flatShading: !isFrozen,
    }),
  );
  mesh.scale.setScalar(node.size);
  if (isFrozen) mesh.rotation.set(0.5, 0.7, 0.2);
  return mesh;
}

/* ── 节点光晕 ── */
export function createGlowSprite(node: StarLayoutNode): THREE.Sprite {
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: getGlowTexture(),
      color: node.color,
      transparent: true,
      opacity: node.isCenter ? 0.42 : 0.24,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
    }),
  );
  const scale = node.isCenter ? 20 : 4.2 + node.size * 2.4;
  sprite.scale.set(scale, scale, 1);
  sprite.position.copy(node.pos);
  return sprite;
}

/* ── 绕节点星轨 ── */
export function createNodeTrail(node: StarLayoutNode): THREE.Mesh {
  const trail = new THREE.Mesh(
    new THREE.TorusGeometry(node.size * 1.9, 0.045, 6, 40),
    new THREE.MeshBasicMaterial({
      color: node.color,
      transparent: true,
      opacity: 0.34,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  trail.position.copy(node.pos);
  trail.rotation.x = Math.random() * Math.PI;
  trail.rotation.y = Math.random() * Math.PI;
  trail.userData.spin = (Math.random() - 0.5) * 0.7;
  return trail;
}
